import React, { useState, useEffect } from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { GridFooterContainer } from '@mui/x-data-grid';
import { Box, Button, Select, MenuItem, Typography, FormControl, Pagination, PaginationItem, IconButton } from '@mui/material';
import '../components/DeviceTable.css';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import AccountManagementDialog from './AccountManagementDialog'; // Import your custom dialog component
import koKR from '../components/koKR.json'; // Import the translation file
import AccountCreateDialog from './AccountCreateDialog';
import CustomColumnSortedAscendingIcon from '../components/CustomColumnSortedAscendingIcon';
import CustomColumnSortedDescendingIcon from '../components/CustomColumnSortedDescendingIcon';
import apiClient from '../api/apiClient'; // API client import
import Config from '../Config'; // apiUrl 추가
import { getAccessToken } from '../utils/token';

const apiUrl = Config.apiUrl;

function CustomPagination(props) {
    const pageCount = Math.ceil(props.totalElements / props.size);
    const rowsPerPage = props.size;

    const handlePageChange = (event, value) => {
        props.onPageChange(value - 1); // DataGrid는 0-based page, Pagination은 1-based page
    };

    const handlePageSizeChange = (event) => {
        props.onPageSizeChange(parseInt(event.target.value, 10));
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px', width: '100%', marginBottom: '25px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1">Showing</Typography>
                <FormControl variant="outlined" sx={{ mx: 1, minWidth: 78, width: 78, height: 38 }}>
                    <Select value={rowsPerPage} onChange={handlePageSizeChange}
                        disableUnderline
                        sx={{ fontSize: '0.875rem', height: '100%', paddingTop: '8px', paddingBottom: '8px' }}>
                        {[10, 20, 30, 50].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body1">of {props.totalElements}</Typography>
            </Box>
            {props.totalElements > 0 && (
                <Pagination
                    count={pageCount}
                    page={props.page + 1}
                    onChange={handlePageChange}
                    siblingCount={1}
                    boundaryCount={1}
                    shape="rounded"
                    color="primary"
                    variant='outlined'
                    renderItem={(item) => (
                        <PaginationItem
                            {...item}
                            sx={{
                                '&.Mui-selected': {
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                },
                                backgroundColor: item.selected ? 'initial' : '#e0e0e0',
                                color: item.selected ? '#fff' : '#8b909a', // 선택되지 않았을 때 글자색 설정
                                border: 'none',
                                width: '28px',    // 가로 크기 설정
                                height: '28px',   // 세로 크기 설정
                                margin: '1px',    // 박스 간 간격 설정 (전체 간격이 2px이므로 양쪽에 1px씩)
                            }}
                        />
                    )}
                />
            )}
        </Box>
    );
}

const NoRowsOverlay = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    fontSize: '1.5rem',
    color: '#888',
    flexDirection: 'column',
});

function CustomFooter(props) {
    return (
        <GridFooterContainer sx={{ borderTop: 'none', color: '#8b909a' }}>
            <CustomPagination
                page={props.page}
                size={props.size}
                totalElements={props.totalElements}
                onPageChange={props.onPageChange}
                onPageSizeChange={props.onPageSizeChange}
            />
        </GridFooterContainer>
    );
}

const AccountList = () => {
    const [rows, setRows] = useState([]);
    const apiRef = useGridApiRef();
    const { i18n } = useTranslation();
    const { t } = useTranslation('console');
    const getRowHeight = (params) => 58;
    const [selectionModel, setSelectionModel] = React.useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [hoveredRow, setHoveredRow] = useState(null); // Track hovered row
    const [open, setOpen] = useState(false);
    const [openDiag, setOpenDiag] = useState(false);

    // Pagination and sorting states
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0); // 0-based page index
    const [size, setSize] = useState(10); // Page size
    const [sort, setSort] = useState([
        "createdAt,desc",
    ]); // Default sort
    const [totalElements, setTotalElements] = useState(0); // Total number of elements for pagination

    // API 호출로 데이터를 가져오는 부분
    const fetchAccountList = async () => {
        setLoading(true);
        try {
            const token = getAccessToken();
            const response = await apiClient.get(`${apiUrl}/console/users`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    page: page,
                    size: size,
                    sort: sort,
                },
            });

            const fetchedRows = response.data.content.map((user) => ({
                id: user.id,
                name: user.name,
                email: user.email,
                department: user.department,
                grade: user.role === 'ADMIN' ? 'A' : 'M', // role에 따라 grade 설정
                auth_code: user.authCode, // API에 authCode 필드가 있다고 가정
                created_date: user.createdAt, // API 필드명과 일치
                management: 'manage',
            }));
            setRows(fetchedRows);
            setTotalElements(response.data.totalElements); // 총 데이터 수 설정
        } catch (error) {
            console.error('Error fetching account list:', error);
        } finally {
            setLoading(false);
        }
    };

    // 컴포넌트가 마운트될 때 fetchAccountList 호출
    useEffect(() => {
        fetchAccountList();
    }, [page, size, sort]);

    const handleIconClick = (params) => {
        setSelectedRow(params.row);
        setOpen(true);  // Open the AccountManagementDialog
    };

    const handleClose = () => {
        setOpen(false);  // Close the AccountManagementDialog
    };

    const columns = [
        { field: 'id', headerName: `${t('account_list.column.id')}`, flex: 1, minWidth: 70, headerAlign: 'center', align: 'center' },
        { field: 'name', headerName: `${t('account_list.column.name')}`, flex: 1.5, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'email', headerName: `${t('account_list.column.email')}`, flex: 2, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'department', headerName: `${t('account_list.column.department')}`, flex: 1.5, minWidth: 100, headerAlign: 'center', align: 'center' },
        {
            field: 'grade', headerName: `${t('account_list.column.grade')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center',
            renderCell: (params) => (
                <img
                    src={'M' === params.value ? './icon-admin.svg' : './icon-user.svg'} // Change icon based on hover state
                    alt="grade icon"
                    style={{ width: 40, height: 40 }}
                />
            ),
        },
        //{ field: 'auth_code', headerName: `${t('account_list.column.auth_code')}`, flex: 1.5, minWidth: 100, headerAlign: 'center', align: 'center' }, // auth_code 컬럼 추가
        //{ field: 'created_date', headerName: `${t('account_list.column.created_at')}`, flex: 1.5, minWidth: 100, headerAlign: 'center', align: 'center' },
        {
            field: 'management', headerName: `${t('account_list.column.management')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center',
            sortable: false,
            renderCell: (params) => (
                <IconButton
                    onClick={() => handleIconClick(params)}
                    onMouseEnter={() => setHoveredRow(params.id)} // Set hovered row
                    onMouseLeave={() => setHoveredRow(null)} // Remove hovered row
                >
                    <img
                        src={hoveredRow === params.id ? './button_setting_hover.png' : './button_setting_default.png'} // Change icon based on hover state
                        alt="custom icon"
                        style={{ width: 40, height: 40 }}
                    />
                </IconButton>
            ),
        },
    ];

    const handleCreateAccount = () => {
        console.log('Create Account');
        setOpenDiag(true);
    };

    const handleDiagClose = () => {
        setOpenDiag(false);
        fetchAccountList(); // 리스트를 새로 고침
    }

    const getLocaleText = () => {
        return i18n.language === 'ko' ? koKR : {};
    };

    return (
        <Box sx={{ width: '1592px', minWidth: 1024 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h2" sx={{ fontSize: '32px', color: '#002a70', fontWeight: 'bold', mt: '20px' }}>
                    {t('account_list.header.title')}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', width: '1524px' }}>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '10px', width: 'calc(100%  - 10px)' }}>
                    <Box className="button_container" sx={{ display: 'flex', gap: '2px' }}>
                        <Button
                            variant="contained"
                            onClick={handleCreateAccount}
                            className="button_create"
                            sx={{
                                width: '122px',
                                height: '48px',
                                fontFamily: 'Pretendard',
                                fontSize: '14px',
                                fontWeight: '500px',
                                borderRadius: '10px',
                                textTransform: 'none',
                                backgroundColor: '#80befc',
                                '&:hover': {
                                    backgroundColor: '#0b5cd5',
                                },
                            }}
                            endIcon={<img src={'/guest.png'} alt="guest Icon" style={{ width: '30px', height: '30px' }} />}
                        >
                            {t('button.add_member')}
                        </Button>
                        <AccountCreateDialog
                            open={openDiag}
                            handleClose={handleDiagClose}
                            onSuccess={fetchAccountList} // 성공 시 리스트 갱신
                        />
                    </Box>
                </Box>
            </Box>
            <Box sx={{ width: '1524px', height: '756px', mt: 0 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    apiRef={apiRef}
                    getRowId={(row) => row.id}
                    getRowHeight={getRowHeight}
                    headerHeight={48}
                    localeText={getLocaleText()} // Use the localeText based on the current locale
                    loading={loading}
                    rowCount={totalElements}
                    pageSizeOptions={[10, 20, 30, 50]}
                    paginationModel={{ page, pageSize: size }}
                    onPaginationModelChange={(newModel) => {
                        setPage(newModel.page);
                        setSize(newModel.pageSize);
                    }}
                    sortingMode="server"
                    sortModel={sort.map(s => ({ field: s.split(',')[0], sort: s.split(',')[1] }))}
                    onSortModelChange={(newSortModel) => {
                        setSort(newSortModel.map(s => `${s.field},${s.sort}`));
                    }}
                    paginationMode="server"
                    slots={{
                        footer: () => (
                            <CustomFooter
                                page={page}
                                size={size}
                                totalElements={totalElements}
                                onPageChange={setPage}
                                onPageSizeChange={setSize}
                            />
                        ),
                        noRowsOverlay: () => (loading ? null : (
                            <NoRowsOverlay>
                                <img src="nodata.png" alt="No data" />
                            </NoRowsOverlay>
                        )),
                        columnSortedAscendingIcon: CustomColumnSortedAscendingIcon,
                        columnSortedDescendingIcon: CustomColumnSortedDescendingIcon,
                    }}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    disableSelectionOnClick
                    selectionModel={selectionModel}
                    onSelectionModelChange={(newSelection) => {
                        setSelectionModel(newSelection);
                    }}

                    sx={{
                            '& .MuiDataGrid-columnHeaders div[role="row"]': {
                                backgroundColor: '#F5F5F7',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                textAlign: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                                fontSize: '14px',
                                fontWeight: '800',
                                color: '#7d7d7d',
                            },
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                fontSize: '16px',
                                color: '#494949',
                                height: '58px',
                                backgroundColor: '#ffffff',
                            },
                            '& .MuiDataGrid-cellContent': {
                                width: '100%',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                display: 'flex',
                                justifyContent: 'center',
                                marginTop: '50px',
                                backgroundColor: '#ffffff',
                            },
                            borderBottomLeftRadius: '14px',
                            borderBottomRightRadius: '14px',
                            overflow: 'hidden',
                            backgroundColor: '#ffffff',
                        }}
                    />
                <AccountManagementDialog
                    open={open}
                    handleClose={handleClose}
                    selectedRow={selectedRow} // Pass the selected row to the dialog for context
                    onListRefresh={fetchAccountList} // 리스트 갱신을 위한 콜백 전달
                />
            </Box>
        </Box>
    );
};

export default AccountList;